import {
  ConfigCapability,
  Role,
  RoleId,
  type ConfigSlot,
  type DeviceManifest,
} from '../types/manifest'

export interface SlotInfo {
  id: number
  roles: Role[]
}

/** 与应用层 `VITE_CHANNEL` / manifest.channel 对齐的网吧渠道。 */
export const CYBERCAFE_MANIFEST_CHANNEL = 'china_cybercafe'

/**
 * 从设备 manifest 中获取 configSlots 中的角色可见槽位列表
 * 没有 configSlots 的返回 null
 * @param roles 当前用户的角色列表，与 slot.roles 有交集即视为可见
 */
export function getRoleMatchedSlots(
  manifestCapabilities: { type: string; config: unknown }[] | undefined,
  roles: Role[],
): SlotInfo[] | null {
  const configCap = manifestCapabilities?.find(
    (item): item is ConfigCapability & { type: string } => item.type === 'config',
  ) as ConfigCapability | undefined
  const slots = configCap?.config.configSlots
  if (!slots) return null
  // roles 为空时不过滤，全部可见
  if (roles.length === 0) return slots
  // Web 端只要账号携带管理员角色，就使用管理员配置视图。
  // SDK 必须保持相同的管理员优先级，否则 [admin, user] 会变成两组槽位的并集，
  // 从而破坏固件相对索引与可见槽位顺序的对应关系。
  const hasAdminRole = roles.includes(RoleId.superAdmin) || roles.includes(RoleId.admin)
  const effectiveRoles = hasAdminRole
    ? roles.filter((role) => role === RoleId.superAdmin || role === RoleId.admin)
    : roles
  return slots.filter((slot) => slot.roles.some((r) => effectiveRoles.includes(r)))
}

/**
 * 网吧渠道且声明了 configSlots 时返回槽位列表，否则 null。
 * 空名补写只针对这类设备，与鼠标/键盘/芯片无关。
 */
export function getCybercafeConfigSlots(
  manifest: Pick<DeviceManifest, 'channel' | 'capabilities'>,
): ConfigSlot[] | null {
  if (!manifest.channel?.includes(CYBERCAFE_MANIFEST_CHANNEL)) return null
  const configCap = manifest.capabilities.find(
    (item): item is ConfigCapability => item.type === 'config',
  )
  const slots = configCap?.config.configSlots
  return slots?.length ? slots : null
}

/**
 * 为网吧槽位设备的空名槽写入默认名（UserN / FactoryN）。
 * 调用方须提供「不过滤空名」的列表读取，避免经 getAllConfigs 递归。
 * 只应在设备已可达时调用（如 getAllConfigs），不要挂在 initAllConfigs 末尾。
 */
export async function ensureCybercafeConfigSlotNames(
  slots: ConfigSlot[],
  listUnfilteredConfigs: () => Promise<Array<{ index: number; name: string }>>,
  createConfig: (name: string, index: number) => Promise<number>,
): Promise<void> {
  const allConfigs = await listUnfilteredConfigs()
  const byIndex = new Map(allConfigs.map((item) => [item.index, item]))

  const isAdminSlot = (slot: ConfigSlot) =>
    slot.roles.includes(RoleId.superAdmin) || slot.roles.includes(RoleId.admin)
  const adminSlots = slots.filter(isAdminSlot)
  const userSlots = slots.filter((slot) => !isAdminSlot(slot))

  for (const slot of slots) {
    const existing = byIndex.get(slot.id)
    if (existing?.name?.trim()) continue

    const group = isAdminSlot(slot) ? adminSlots : userSlots
    const pos = group.findIndex((item) => item.id === slot.id) + 1
    const name = isAdminSlot(slot) ? `Factory${pos}` : `User${pos}`
    await createConfig(name, slot.id)
  }
}

/**
 * 将外部传入的绝对 slot id 转换为固件实际使用的 index
 * 管理员模式下固件按相对位置计算，需要转换
 */
export function toFirmwareIndex(absoluteIndex: number, matchedSlots: SlotInfo[] | null): number {
  if (!matchedSlots) return absoluteIndex
  const pos = matchedSlots.findIndex((s) => s.id === absoluteIndex)
  return pos !== -1 ? pos : absoluteIndex
}

/**
 * 将固件返回的 index 转换为对外一致的绝对 slot id
 */
export function fromFirmwareIndex(firmwareIndex: number, matchedSlots: SlotInfo[] | null): number {
  if (!matchedSlots) return firmwareIndex
  if (firmwareIndex >= 0 && firmwareIndex < matchedSlots.length) {
    return matchedSlots[firmwareIndex].id
  }
  return firmwareIndex
}

/**
 * 将固件上报的「当前角色可见槽位中的相对位置」转为全局唯一的绝对槽位 ID。
 *
 * 该转换按 configSlots 顺序映射，因此同时支持：
 * - 用户 0~3 / 管理员 4~7 这类完全分组；
 * - 同一槽位同时对用户和管理员可见的共享槽位；
 * - 未声明 configSlots 的设备（原值返回）。
 */
export function toAbsoluteConfigIndex(
  firmwareIndex: number,
  manifestCapabilities: { type: string; config: unknown }[] | undefined,
  roles: Role[],
): number {
  return fromFirmwareIndex(firmwareIndex, getRoleMatchedSlots(manifestCapabilities, roles))
}
